import PropTypes from "prop-types";
import { useState, useRef, useEffect, useCallback } from "react";
import { getThemeWithMedia, DarkTheme, Link } from "@a0/quantum-web";
import { ThemeProvider } from "styled-components";
import mediaSizes from "../shared/mediaSizes";
import Mobile from "./Mobile";
import Desktop from "./Desktop";
import * as S from "./styled";

const MESSAGE_BAR_STATE = {
  CLOSED: "CLOSED",
  OPEN: "OPEN",
};

const MESSAGE_BAR_STATUS = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
};

const messageBar = {
  status: MESSAGE_BAR_STATUS.INACTIVE,
  cta: {
    message: "Missed DevDay24? Register for the Best of DevDay",
    url: "https://pages.okta.com/2024-11-WBN-Best-of-Dev-Day_LP/",
  },
  id: {
    key: "messageBar_id",
    value: "BEST_OF_DEVDAY_2024",
  },
  state: {
    key: "messageBar_state",
  },
};

const Header = ({ dark, devHomeUrl }) => {
  const [hasScrolled, setHasScrolled] = useState(false);
  // This state is only used when banner is set
  // eslint-disable-next-line no-unused-vars
  // const [hideTopBar, setHideTopBar] = useState(true);
  const observedPixel = useRef(null);

  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [canRenderMessageBar, setCanRenderMessageBar] = useState(false);

  const toggleIsMobileMenuOpen = useCallback(() => {
    setMobileMenuOpen(!isMobileMenuOpen);
  }, [isMobileMenuOpen]);

  const closeMessageBar = () => {
    const isMessageBarActive = messageBar.status === MESSAGE_BAR_STATUS.ACTIVE;

    if (!isMessageBarActive) {
      return;
    }

    window.localStorage.setItem(messageBar.state.key, MESSAGE_BAR_STATE.CLOSED);

    setCanRenderMessageBar(false);
  };

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    let messageBarId = window.localStorage.getItem(messageBar.id.key);
    let messageBarState = window.localStorage.getItem(messageBar.state.key);

    if (!messageBarId) {
      window.localStorage.setItem(messageBar.id.key, messageBar.id.value);
      messageBarId = window.localStorage.getItem(messageBar.id.key);
    }

    if (!messageBarState) {
      window.localStorage.setItem(messageBar.state.key, MESSAGE_BAR_STATE.OPEN);
      messageBarState = window.localStorage.getItem(messageBar.state.key);
    }

    switch (messageBar.status) {
      case MESSAGE_BAR_STATUS.ACTIVE: {
        const isExistingCta = messageBarId === messageBar.id.value;

        if (!isExistingCta) {
          window.localStorage.setItem(messageBar.id.key, messageBar.id.value);
          window.localStorage.setItem(
            messageBar.state.key,
            MESSAGE_BAR_STATE.OPEN,
          );

          setCanRenderMessageBar(true);

          return;
        }

        switch (messageBarState) {
          case MESSAGE_BAR_STATE.OPEN: {
            setCanRenderMessageBar(true);

            return;
          }
          default: {
            setCanRenderMessageBar(false);

            return;
          }
        }
      }
      default: {
        setCanRenderMessageBar(false);

        return;
      }
    }
  }, []);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: [1],
    };

    if (!window.IntersectionObserver) {
      setHasScrolled(true);
      return;
    }

    const observer = new IntersectionObserver((observers) => {
      if (!observers[0].isIntersecting) {
        setHasScrolled(true);
        return;
      }
      setHasScrolled(false);
    }, options);

    observer.observe(observedPixel.current);
  }, [observedPixel]);

  return (
    <>
      <S.ObservedPixel ref={observedPixel} />
      <S.Spacer hide={!canRenderMessageBar} />
      <ThemeProvider theme={getThemeWithMedia(DarkTheme, mediaSizes)}>
        <S.Header
          dark={dark}
          hasScrolled={hasScrolled}
          isMobileMenuOpen={isMobileMenuOpen}
        >
          <S.TopBanner hide={!canRenderMessageBar}>
            <S.TopBannerContainer>
              <Link
                size="xsmall"
                href={messageBar.cta.url}
                arrow
                styleOverrides={{ color: "white", fontWeight: 600 }}
                target="_blank"
              >
                {messageBar.cta.message}
              </Link>
              <S.CloseButton onClick={closeMessageBar}>+</S.CloseButton>
            </S.TopBannerContainer>
          </S.TopBanner>
          <Mobile
            toggleIsMobileMenuOpen={toggleIsMobileMenuOpen}
            isMobileMenuOpen={isMobileMenuOpen}
            devHomeUrl={devHomeUrl}
          />
          <Desktop
            dark={dark}
            devHomeUrl={devHomeUrl}
            topBannerOpen={canRenderMessageBar}
          />
        </S.Header>
      </ThemeProvider>
    </>
  );
};

Header.propTypes = {
  dark: PropTypes.bool.isRequired,
  devHomeUrl: PropTypes.string,
};

Header.defaultProps = {
  devHomeUrl: "https://developers.auth0.com/",
};

export default Header;
